import React, { useState, useEffect } from "react";
import { User, LoginResponse, Address } from "../types";
import { useTranslation } from "react-i18next";
import { getProfile, updateProfile } from "../api/auth";
import {
  User as UserIcon,
  Camera,
  MapPin,
  Mail,
  Calendar,
  Utensils,
  AlertTriangle,
} from "lucide-react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { toast } from "sonner";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

interface AddressValue {
  label: string;
  value: {
    description: string;
    place_id: string;
    reference: string;
    structured_formatting: {
      main_text: string;
      secondary_text: string;
    };
    terms: {
      offset: number;
      value: string;
    }[];
    types: string[];
  };
}

const UpdateProfile: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [addressValue, setAddressValue] = useState<AddressValue | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const simple = localStorage.getItem("user");
      const simpleUserData: LoginResponse | null = simple
        ? JSON.parse(simple)
        : null;
      const userId = simpleUserData?.id;
      if (!userId) {
        setError("User ID not found. Please log in.");
        return;
      }
      const { data, error } = await getProfile(userId);
      if (error) {
        setError(error.message);
      } else if (data) {
        console.log("Fetched user data:", data);
        setUser(data);
        setFormData({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          dateOfBirth: data.dateOfBirth,
          preferences: data.preferences,
          // allergies: data.allergies,
          allergies: data.allergies,
          address:
            data.address ||
            ({
              street: "",
              city: "",
              country: "",
              zipCode: "",
              fullAddress: "",
            } as Address),
        });
        if (data.profileImageUrl) {
          setPhotoPreview(data.profileImageUrl);
        }
        if (data.address) {
          setAddressValue({
            label: data.address.fullAddress,
            value: {
              description: data.address.fullAddress,
              place_id: "",
              reference: "",
              structured_formatting: {
                main_text: data.address.street || "",
                secondary_text: `${data.address.city || ""}, ${
                  data.address.country || ""
                }`.trim(),
              },
              terms: [],
              types: [],
            },
          });
        }
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddressSelect = (value: AddressValue | null) => {
    setAddressValue(value);
    if (!value || !value.value || !value.value.place_id) {
      setFormData((prev) => ({
        ...prev,
        address: undefined,
      }));
      return;
    }
    const placeId = value.value.place_id;

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error("Google Maps JavaScript API script is not loaded.");
      toast.error("Could not connect to Google Maps service.");
      return;
    }

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );
    service.getDetails(
      {
        placeId: placeId,
        fields: ["address_component", "geometry.location"],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry &&
          place.geometry.location &&
          place.address_components
        ) {
          const latLng = place.geometry.location;
          const addressComponents = place.address_components;

          const streetNumber =
            addressComponents.find((c) => c.types.includes("street_number"))
              ?.long_name || "";
          const route =
            addressComponents.find((c) => c.types.includes("route"))
              ?.long_name || "";
          const street = `${streetNumber} ${route}`.trim();
          const city =
            addressComponents.find((c) => c.types.includes("locality"))
              ?.long_name || "";
          const country =
            addressComponents.find((c) => c.types.includes("country"))
              ?.long_name || "";
          const zipCode =
            addressComponents.find((c) => c.types.includes("postal_code"))
              ?.long_name || "";

          setFormData((prev) => ({
            ...prev,
            address: {
              street,
              city,
              country,
              zipCode,
              latitude: latLng.lat(),
              longitude: latLng.lng(),
              fullAddress: value.label,
            },
          }));
        } else {
          console.error(`Error fetching place details. Status: ${status}`);
          toast.error(`Could not fetch address details. Status: ${status}`);
        }
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    const updateData = {
      ...formData,
      allergies:
        typeof formData?.allergies === "string"
          ? formData.allergies
              .split(",")
              .map((a) => a.trim())
              .filter(Boolean)
          : formData?.allergies,
      preferences:
        typeof formData?.preferences === "string"
          ? formData.preferences
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean)
          : formData?.preferences,
      photo: photoFile ?? undefined,
    };

    const { data, error } = await updateProfile(updateData);
    if (error) {
      setError(error.message);
      setSuccess(null);
      toast.error(`Update failed: ${error.message}`);
    } else if (data) {
      setUser(data);
      setSuccess("Profile updated successfully!");
      toast.success("Profile updated successfully!");
      setError(null);
      if (data.profileImageUrl) {
        setPhotoPreview(data.profileImageUrl);
      }
    }
  };

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center text-red-600">
        <h1 className="text-2xl font-bold mb-4">{t("updateProfile.title")}</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto p-4 text-center text-gray-600">
        <h1 className="text-2xl font-bold mb-4">{t("updateProfile.title")}</h1>
        <p>{t("profile.notLoggedIn")}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        {t("updateProfile.title")}
      </h1>
      {success && (
        <div className="bg-green-100 text-green-700 p-4 rounded mb-4">
          {success}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-lg rounded-xl p-6 md:p-8"
      >
        <div className="flex justify-center pb-4">
          <div className="relative">
            <label htmlFor="photo-upload" className="cursor-pointer group">
              <div className="w-28 h-28 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:border-orange-500 group-hover:bg-gray-50">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserIcon className="w-12 h-12 text-gray-400 transition-all duration-300 group-hover:text-orange-500" />
                )}
              </div>
              <div className="absolute bottom-0 right-0 bg-orange-500 p-2 rounded-full text-white shadow-md transform group-hover:scale-110 transition-transform duration-300">
                <Camera className="w-5 h-5" />
              </div>
            </label>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("auth.firstName")}
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="firstName"
                value={formData.firstName || ""}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                placeholder={t("auth.yourFirstName")}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("auth.lastName")}
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="lastName"
                value={formData.lastName || ""}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                placeholder={t("auth.yourLastName")}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("auth.emailAddress")}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email || ""}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                placeholder={t("auth.yourEmail")}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("auth.phone")}
            </label>
            <PhoneInput
              international
              defaultCountry="CA"
              value={formData.phone || ""}
              onChange={(value) =>
                setFormData({ ...formData, phone: value || "" })
              }
              required
              className="phone-input-custom"
              placeholder={t("auth.phonePlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("auth.dateOfBirth")}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                name="dateOfBirth"
                value={
                  formData.dateOfBirth
                    ? new Date(formData.dateOfBirth).toISOString().split("T")[0]
                    : ""
                }
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
              />
            </div>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("auth.preferences")}
          </label>
          <div className="relative">
            <Utensils className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <textarea
              name="preferences"
              value={formData.preferences || ""}
              onChange={handleChange}
              rows={2}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none text-sm"
              placeholder="e.g., spicy food, vegetarian (comma-separated)"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("auth.allergies")}
          </label>
          <div className="relative">
            <AlertTriangle className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <textarea
              name="allergies"
              value={
                Array.isArray(formData.allergies)
                  ? formData.allergies.join(", ")
                  : formData.allergies || ""
              }
              onChange={handleChange}
              rows={2}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none text-sm"
              placeholder="e.g., peanuts, shellfish (comma-separated)"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("auth.address")}
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <GooglePlacesAutocomplete
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              autocompletionRequest={{
                componentRestrictions: {
                  country: ["ca"],
                },
                types: ["address"],
              }}
              selectProps={{
                value: addressValue,
                onChange: handleAddressSelect,
                placeholder: t("auth.addressPlaceholder"),
                styles: {
                  control: (base, state) => ({
                    ...base,
                    width: "100%",
                    paddingLeft: "2.5rem",
                    borderWidth: "1px",
                    borderColor: state.isFocused ? "#f97316" : "#e5e7eb",
                    borderRadius: "0.5rem",
                    backgroundColor: state.isFocused ? "#ffffff" : "#f9fafb",
                    boxShadow: state.isFocused ? "0 0 0 2px #f97316" : "none",
                    "&:hover": {
                      borderColor: state.isFocused ? "#f97316" : "#d1d5db",
                    },
                  }),
                  input: (base) => ({
                    ...base,
                    margin: "0",
                    padding: "0",
                    height: "2.5rem",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused
                      ? "#fed7aa"
                      : state.isSelected
                      ? "#fb923c"
                      : "#ffffff",
                    color: state.isSelected ? "#ffffff" : "#1f2937",
                    "&:hover": {
                      backgroundColor: "#fed7aa",
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                  }),
                },
                classNamePrefix: "react-select",
              }}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" className="btn-primary">
            {t("updateProfile.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateProfile;
